// online-status.ts (Story 7.7) — the real `isEffectivelyOnline()` derivation (NFR26).
//
// Replaces the deliberate `navigator.onLine`-only throwaway 7.8 left in
// `draft-producer-runner.ts`. Connectivity is NEVER `navigator.onLine` alone: Safari on
// macOS reports a stale `true`, which is the whole reason NFR26 exists. The derivation is
// first-decisive-wins:
//   1. `meta.force_offline === true`        → false   (R6 harness flag; NO network attempt)
//   2. `navigator.onLine === false`         → false   (cheap, reliable-when-false; skip probe)
//   3. last drain wholly transport-failed   → false   (a real drain attempt just failed)
//   4. `GET /health` probe (≤3s)            → true on 2xx, else false   (authoritative positive)
//
// The probe is a RAW `fetch("/health")`: the FastAPI liveness route lives at the ROOT
// (`src/infrastructure/main.py:379`), NOT under `/api/v1`, so `apiFetch` (which prefixes
// `/api/v1`) cannot reach it. It is unauthenticated and `cache: "no-store"` so the Service
// Worker precache can't answer a stale 200 while the network is actually down.
//
// DEV NUANCE: the Vite dev proxy only forwards `/api` (vite.config.ts), so under `npm run dev`
// `fetch("/health")` hits the Vite server (index.html, 200) → a false-positive "online". That is
// exactly why the documented dev-offline driver is `meta.force_offline` (step 1), which
// short-circuits BEFORE any probe. In production the SPA + API are same-origin, so `/health`
// reaches the backend and the probe is authoritative.
//
// The epic AC text says `/healthz`; that route does not exist on the API. Documented
// correction — use `GET /health`. [7-4 Dev Notes]

import { useEffect, useState } from "react";

import { DRAIN_COMPLETE_EVENT } from "./drain-runner";
import type { DrainStats } from "./drain-core";
import { getMeta } from "./idb-schema";

/** The root liveness route (NOT `/api/v1/health`, NOT `/healthz`). */
const HEALTH_URL = "/health";

/** Short-timeout probe — a hung connection must read offline quickly, not block the gate. */
const PROBE_TIMEOUT_MS = 3000;

// --- Recent-drain signal -----------------------------------------------------
// A drain where EVERY attempted op failed with no server answer is a strong, real offline
// signal (the network just rejected actual writes). A mixed/clean drain means we are online.
// This biases `isEffectivelyOnline()` to `false` so the banner + gate react to an actual
// drain attempt, not only a manual probe (AC2).

let lastDrainWasOffline = false;
// When the offline bias was last set (ms epoch). The bias short-circuits the probe (step 3) only
// briefly: `online`/`offline` events are unreliable (the very premise of NFR26), so a reconnect
// that fires no `online` event and leaves an empty queue (no further drain) must still recover.
// After this TTL the derivation falls through to the probe; a confirmed-online probe then clears
// the bias outright (step 4).
let lastDrainOfflineAt = 0;
// Exported so the test asserts the real TTL rather than mirroring a hand-copied literal (a copy
// would keep a tuned-down value green and give a false pass). [PR #442 review — womanonrails #1]
export const DRAIN_OFFLINE_TTL_MS = 30_000;

function isOfflineDrain(stats: DrainStats): boolean {
  return (
    stats.queued > 0 &&
    stats.failed === stats.queued &&
    stats.succeeded === 0 &&
    stats.conflicts === 0 &&
    stats.adopted === 0
  );
}

// Track drain outcomes + clear the bias on reconnect so it can never get stuck `true` when no
// further drain runs (e.g. queue drained empty). Guarded for a non-DOM realm.
//
// Handlers are NAMED (not inline) so the Vite HMR dispose hook below can deregister exactly these
// refs before a hot re-eval re-registers fresh ones — otherwise a dev edit double-registers the
// listeners and the drain bias mutates twice per event. [PR #442 review — womanonrails #2]
// NOTE: `removeEventListener` matches by function identity, so the reviewer's suggested
// "remove-then-add of fresh refs at module top" can't work (the *previous* module's closures are
// gone) — `import.meta.hot.dispose`, which captures THIS instance's refs, is the only correct seam.
const handleDrainComplete = (event: Event) => {
  const stats = (event as CustomEvent<DrainStats>).detail;
  if (stats) {
    lastDrainWasOffline = isOfflineDrain(stats);
    if (lastDrainWasOffline) lastDrainOfflineAt = Date.now();
  }
};
const handleOnline = () => {
  lastDrainWasOffline = false;
};

if (typeof window !== "undefined") {
  window.addEventListener(DRAIN_COMPLETE_EVENT, handleDrainComplete);
  window.addEventListener("online", handleOnline);

  // Dev-only: drop this instance's listeners when Vite hot-replaces the module (production is a
  // single page-lifetime load, so the `if` is dead there and in vitest where `import.meta.hot` is
  // undefined). Closes the double-registration window React Fast Refresh would normally hide by
  // full-reloading this non-component module.
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      window.removeEventListener(DRAIN_COMPLETE_EVENT, handleDrainComplete);
      window.removeEventListener("online", handleOnline);
    });
  }
}

/** Test-only: reset the module-level recent-drain bias between cases. */
export function __resetOnlineStatusForTests(): void {
  lastDrainWasOffline = false;
  lastDrainOfflineAt = 0;
}

// --- The probe ---------------------------------------------------------------

async function probeHealth(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(HEALTH_URL, {
      method: "GET",
      signal: controller.signal,
      cache: "no-store",
    });
    return res.ok;
  } catch {
    // Network error, timeout/abort, or a thrown response → offline.
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Derive REAL connectivity (NFR26). Async because the authoritative step is a network probe;
 * callers that cannot await (render) use `useEffectiveOnline()` which holds the last-known value.
 *
 * Consumed by the producer via DI (`draft-producer-runner.ts` re-exports this as
 * `buildProducerDeps().isOnline`), so swapping in this real derivation touches no producer-core
 * code — `ProducerDeps.isOnline` already accepts the async form.
 */
export async function isEffectivelyOnline(): Promise<boolean> {
  // 1. Harness override — no network attempt at all.
  if ((await getMeta<boolean>("force_offline")) === true) return false;
  // 2. Fast negative — `navigator.onLine === false` is trustworthy; skip the probe.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  // 3. A real drain just wholly failed at the transport layer → bias offline, but only briefly:
  //    `online` events are unreliable, so after a TTL fall through to the probe (otherwise a
  //    reconnect that fired no `online` event and left an empty queue would stick "offline").
  if (lastDrainWasOffline && Date.now() - lastDrainOfflineAt < DRAIN_OFFLINE_TTL_MS) return false;
  // 4. Authoritative positive — probe the liveness route. A confirmed-online probe clears any
  //    stale drain bias so the next call doesn't re-block on it.
  const online = await probeHealth();
  if (online) lastDrainWasOffline = false;
  return online;
}

/**
 * Reactive connectivity for components. Holds the last-known boolean (optimistic — never blocks
 * render on an in-flight probe) and re-derives on the SAME signal set 7.5's `useEvaluationConflict`
 * established: `online`/`offline`, the `franklin:drain-complete` window event, `focus`, and
 * `visibilitychange`. Initial state is the optimistic `navigator.onLine` value, corrected by the
 * first async derivation.
 */
export function useEffectiveOnline(): boolean {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" ? true : navigator.onLine !== false,
  );

  useEffect(() => {
    let cancelled = false;
    const rederive = () => {
      void isEffectivelyOnline().then((value) => {
        if (!cancelled) setOnline(value);
      });
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") rederive();
    };

    rederive(); // initial correction

    window.addEventListener("online", rederive);
    window.addEventListener("offline", rederive);
    window.addEventListener(DRAIN_COMPLETE_EVENT, rederive);
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener("online", rederive);
      window.removeEventListener("offline", rederive);
      window.removeEventListener(DRAIN_COMPLETE_EVENT, rederive);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return online;
}
